/**
 * Community User Query Keys Factory
 * Centralized query key generation for community user-related queries
 * Used by both query stores and middleware
 */

/**
 * Creates query key for user by slug
 * @param userSlug - User slug identifier
 * @returns Query key array
 */
export function createUserBySlugQueryKey(userSlug: string): readonly unknown[] {
  return ['community-user', 'slug', userSlug] as const
}

/**
 * Creates query key for user group details
 * @param groupId - Group ID
 * @param locationId - Location ID
 * @returns Query key array
 */
export function createUserGroupDetailsQueryKey(groupId: string, locationId: string): readonly unknown[] {
  return ['community-user', locationId, 'user-group-details', groupId] as const
}

/**
 * Creates query key for user group details by contact ID
 * @param contactId - Contact ID
 * @param groupId - Group ID
 * @param locationId - Location ID
 * @returns Query key array
 */
export function createUserGroupDetailsByContactQueryKey(contactId: string, groupId: string, locationId: string): readonly unknown[] {
  return ['community-user', locationId, 'group-user-details', groupId, contactId] as const
}

/**
 * Creates query key for user groups
 * @param contactId - Contact ID
 * @param offset - Offset for pagination
 * @param limit - Limit for pagination
 * @param role - Optional role filter
 * @returns Query key array
 */
export function createUserGroupsQueryKey(
  contactId: string,
  offset: number,
  limit: number,
  skipPagination: boolean,
  role?: string
): readonly unknown[] {
  return ['community-user', 'user-groups', contactId, offset, limit, skipPagination, role] as const
}

/**
 * Creates query key for user common groups
 * @param contactId - Contact ID
 * @returns Query key array
 */
export function createUserCommonGroupsQueryKey(contactId: string): readonly unknown[] {
  return ['community-user', 'common-groups', contactId] as const
}

/**
 * Creates query key for follow status lookup
 * @param contactId - Target contact ID
 * @param groupId - Optional group ID context
 * @returns Query key array
 */
export function createFollowStatusQueryKey(contactId: string, groupId?: string): readonly unknown[] {
  return ['community-user', 'follow-status', groupId || '', contactId] as const
}

export function createFollowersQueryKey(contactId: string, locationId: string, limit: number, pageNo: number): readonly unknown[] {
  return ['community-user', 'followers', locationId, contactId, limit, pageNo] as const
}

export function createFollowingQueryKey(contactId: string, locationId: string, limit: number, pageNo: number): readonly unknown[] {
  return ['community-user', 'following', locationId, contactId, limit, pageNo] as const
}

/**
 * Creates query key for user contributions
 * @param contactId - Contact ID
 * @param groupId - Group ID
 * @param lastPaginatedId - Cursor for pagination
 * @param limit - Limit for pagination
 * @param contributionType - Optional contribution type filter
 * @returns Query key array
 */
export const isCommunityUserContributionsInfiniteQueryKeyForGroup = (
  queryKey: readonly unknown[],
  groupId: string,
  locationId: string
): boolean =>
  queryKey.length >= 6 &&
  queryKey[0] === 'community-user' &&
  queryKey[1] === 'contributions' &&
  queryKey[2] === locationId &&
  queryKey[3] === groupId &&
  queryKey[queryKey.length - 1] === 'infinite'

export function createUserContributionsQueryKey(
  contactId: string,
  groupId: string,
  locationId: string,
  lastPaginatedId: string,
  limit: number,
  contributionType?: string
): readonly unknown[] {
  return ['community-user', 'contributions', locationId, groupId, contactId, lastPaginatedId, limit, contributionType] as const
}

export function createUserActivityStatsQueryKey(locationId?: string): readonly unknown[] {
  return ['community-user', 'activity-stats', locationId] as const
}

/**
 * Creates query key for gokollab (whitelabel) contactId lookup
 */
export function createGokollabContactIdQueryKey(locationId: string, contactId: string): readonly unknown[] {
  return ['community-user', 'gokollab-contact-id', locationId, contactId] as const
}

/**
 * Partial query key: invalidates all user-groups list queries (any contact / pagination args).
 */
export function createUserGroupsListQueryKeyPrefix(): readonly ['community-user', 'user-groups'] {
  return ['community-user', 'user-groups']
}

/**
 * Community user query keys factory
 */
export const communityUserQueryKeys = {
  userBySlug: createUserBySlugQueryKey,
  userGroupDetails: createUserGroupDetailsQueryKey,
  userGroupDetailsByContact: createUserGroupDetailsByContactQueryKey,
  userGroups: createUserGroupsQueryKey,
  /** Prefix for invalidateQueries — matches every `userGroups` query */
  userGroupsListPrefix: createUserGroupsListQueryKeyPrefix,
  commonGroups: createUserCommonGroupsQueryKey,
  followStatus: createFollowStatusQueryKey,
  followers: createFollowersQueryKey,
  following: createFollowingQueryKey,
  userContributions: createUserContributionsQueryKey,
  userActivityStats: createUserActivityStatsQueryKey,
  gokollabContactId: createGokollabContactIdQueryKey,
} as const
